import { useEffect } from 'react';
import { useConnectionState } from '@livekit/components-react';
import { ConnectionState } from 'livekit-client';

import { publishPresence } from '@/lib/auth/presenceSignal';
import { useAuthStore } from '@/stores/authStore';

/**
 * How often a live call announces that its user is still present. Well under
 * the 2-minute warning lead in IdleLogoutProvider, so a ping always lands
 * before the countdown modal can appear.
 */
export const CALL_KEEPALIVE_MS = 60_000;

/**
 * Keep the idle logout at bay for as long as the LiveKit room is up.
 *
 * The idle timer counts only raw DOM input, and a call produces none: a
 * client who just talks for an hour never fires mousemove, keydown, scroll
 * or touchstart, so the lockout landed exactly at the end of a 60-minute
 * booking. This republishes presence on an interval instead, which also
 * reaches the staff workspace tab that openSessionCallTab left behind.
 *
 * Reconnecting counts as in-call. A transport blip is not the user walking
 * away, and LiveKit settles to Disconnected once it truly gives up.
 *
 * Must be called inside a `<LiveKitRoom>` tree, where useConnectionState
 * resolves.
 */
export function useCallKeepAlive(): void {
  const connectionState = useConnectionState();
  const userId = useAuthStore((s) => s.user?.id) ?? null;

  const inCall =
    connectionState === ConnectionState.Connected ||
    connectionState === ConnectionState.Reconnecting;

  useEffect(() => {
    if (!inCall || userId === null) return;

    publishPresence(userId);
    const id = window.setInterval(() => {
      publishPresence(userId);
    }, CALL_KEEPALIVE_MS);

    return () => {
      window.clearInterval(id);
    };
  }, [inCall, userId]);
}
